import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import "./styles/legal.css";
import { AuthProvider, useAuth } from "./lib/auth";
import ErrorBoundary from "./components/ErrorBoundary";
import ReacceptanceModal from "./components/ReacceptanceModal";
import LegalFooter from "./components/LegalFooter";
import LegalDocument from "./pages/LegalDocument";
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import EarlyAdopterSignup from "./pages/EarlyAdopterSignup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Workspaces from "./pages/Workspaces";
import Community from "./pages/Community";
import MemberProfile from "./pages/MemberProfile";
import MessagesInbox from "./pages/MessagesInbox";
import MessageThread from "./pages/MessageThread";
import Notifications from "./pages/Notifications";
import Profile from "./pages/Profile";
import Admin from "./pages/Admin";
import AdminInventoryVenues from "./pages/AdminInventoryVenues";
import AdminInventoryWorkspaces from "./pages/AdminInventoryWorkspaces";
import AdminVenueEdit from "./pages/AdminVenueEdit";
import AdminVenueTeam from "./pages/AdminVenueTeam";
import AdminAuditLog from "./pages/AdminAuditLog";
import OwnerSetupChecklist from "./pages/OwnerSetupChecklist";
import AdminUngeocodedVenues from "./pages/AdminUngeocodedVenues";
import AdminWorkspaceEdit from "./pages/AdminWorkspaceEdit";
import RecModeListing from "./pages/RecModeListing";
import RecModeConfirmation from "./pages/RecModeConfirmation";
import RecModeDashboard from "./pages/RecModeDashboard";
import BookingV2Success from "./pages/BookingV2Success";
import BookingV2Cancel from "./pages/BookingV2Cancel";
import OwnerDashboard from "./pages/OwnerDashboard";
import OwnerOnboarding from "./pages/OwnerOnboarding";
import SpacesDirectory from "./pages/SpacesDirectory";
import LlmInfo from "./pages/LlmInfo";
import SpaceDetail from "./pages/SpaceDetail";
import VenueWorkspaceDetail from "./pages/VenueWorkspaceDetail";
import ListYourSpace from "./pages/ListYourSpace";
import Partners from "./pages/Partners";
import NominateSpace from "./pages/NominateSpace";
import SubmitEvent from "./pages/SubmitEvent";import Dmv from "./pages/Dmv";
import Region from "./pages/Region";
import Support from "./pages/Support";
import Hours from "./pages/Hours";
import ReviewForm from "./pages/ReviewForm";
import BetaDashboard from "./pages/BetaDashboard";
import PartnerDashboard from "./pages/PartnerDashboard";
import LocalEvents from "./pages/LocalEvents";
import EventDetail from "./pages/EventDetail";
import MemberBookingDetail from "./pages/MemberBookingDetail";
import BetaGate from "./components/BetaGate";
import OnboardingResumeBanner from "./components/OnboardingResumeBanner";
import AdminInventoryV2 from "./pages/AdminInventoryV2";
import AdminLegal from "./pages/AdminLegal";
import AdminEmails from "./pages/AdminEmails";
import AdminCommandCenter from "./pages/AdminCommandCenter";
import AdminInventoryHealth from "./pages/AdminInventoryHealth";
import AdminAlphaUsers from "./pages/AdminAlphaUsers";
import Membership from "./pages/Membership";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import AdminGoNoGo from "./pages/AdminGoNoGo";
import LocationDetail from "./pages/LocationDetail";
import WorkspaceResourceDetail from "./pages/WorkspaceResourceDetail";
import VenueResourceDetail from "./pages/VenueResourceDetail";
import FirstLogin from "./pages/FirstLogin";
import AdminFoundingMembers from "./pages/AdminFoundingMembers";
import AdminAlphaDashboard from "./pages/AdminAlphaDashboard";
import AdminMembers from "./pages/AdminMembers";
import AdminMemberTimeline from "./pages/AdminMemberTimeline";
import AdminStripeStatus from "./pages/AdminStripeStatus";
import Contact from "./pages/Contact";
import Join from "./pages/Join";
import { AMBASSADOR_ENABLED } from "./lib/ambassadorRef";
import AdminAmbassadors from "./pages/AdminAmbassadors";

function Loading() {
  return (
    <div className="m-loading-screen">
      <div className="m-loading-logo"><img src="/brand/metier-logo.png" alt="Métier" /></div>
      <p>Loading…</p>
    </div>
  );
}

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  // Session 57 · Founding Member first-login gate — force new password + profile completion.
  if (user.must_change_password && location.pathname !== "/first-login") {
    return <Navigate to="/first-login" replace />;
  }
  if (adminOnly && user.role !== "admin") return <Navigate to="/workspaces" replace />;
  return children;
}

// Session 60.5 · Root routing (Cam · Feb 2026): unauthenticated visitors to
// workmetier.app root land on /login (conversion). Authenticated visitors
// continue to /spaces. Only affects `/`; every other deep link (password
// reset, event, workspace, invitation, join flows) is UNTOUCHED.
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  return <Navigate to={user ? "/spaces" : "/login"} replace />;
}

function AppRouter() {
  const location = useLocation();
  // Detect OAuth callback synchronously during render (before ProtectedRoute checks)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <BetaGate>
    <OnboardingResumeBanner />
    <ReacceptanceModal />
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/join" element={<Join />} />
      <Route path="/join/early-adopter" element={<EarlyAdopterSignup />} />
      <Route path="/join/early-adopters" element={<Navigate to="/join/early-adopter" replace />} />
      <Route path="/early-adopter" element={<Navigate to="/join/early-adopter" replace />} />
      <Route path="/early-adopters" element={<Navigate to="/join/early-adopter" replace />} />
      {/* Session 42 · /spaces is the single canonical directory (public + members).
          /workspaces is kept as a permanent redirect for legacy links / bookmarks
          so existing email templates and shared links keep working. */}
      <Route path="/workspaces" element={<Navigate to="/spaces" replace />} />
      <Route path="/community" element={<Protected><Community /></Protected>} />
      <Route path="/community/members/:memberId" element={<Protected><MemberProfile /></Protected>} />
      <Route path="/community/messages" element={<Protected><MessagesInbox /></Protected>} />
      <Route path="/community/messages/:threadId" element={<Protected><MessageThread /></Protected>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="/admin/legal" element={<Protected adminOnly><AdminLegal /></Protected>} />
      <Route path="/admin/emails" element={<Protected adminOnly><AdminEmails /></Protected>} />
      <Route path="/admin/command-center" element={<Protected adminOnly><AdminCommandCenter /></Protected>} />
      <Route path="/admin/inventory-health" element={<Protected adminOnly><AdminInventoryHealth /></Protected>} />
      <Route path="/admin/alpha-users" element={<Protected adminOnly><AdminAlphaUsers /></Protected>} />
      <Route path="/admin/founding-members" element={<Protected adminOnly><AdminFoundingMembers /></Protected>} />
      <Route path="/admin/alpha" element={<Protected adminOnly><AdminAlphaDashboard /></Protected>} />
      <Route path="/admin/members" element={<Protected adminOnly><AdminMembers /></Protected>} />
      <Route path="/admin/members/:userId" element={<Protected adminOnly><AdminMemberTimeline /></Protected>} />
      <Route path="/admin/stripe-status" element={<Protected adminOnly><AdminStripeStatus /></Protected>} />
      <Route path="/first-login" element={<Protected><FirstLogin /></Protected>} />
      <Route path="/membership" element={<Membership />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/admin/go-no-go" element={<Protected adminOnly><AdminGoNoGo /></Protected>} />
      <Route path="/admin/beta" element={<Protected adminOnly><BetaDashboard /></Protected>} />
      <Route path="/admin/inventory-v2/preview" element={<Protected adminOnly><AdminInventoryV2 /></Protected>} />
      <Route path="/admin/inventory/venues" element={<Protected adminOnly><AdminInventoryVenues /></Protected>} />
      <Route path="/admin/inventory/workspaces" element={<Protected adminOnly><AdminInventoryWorkspaces /></Protected>} />
      <Route path="/admin/venues/ungeocoded" element={<Protected adminOnly><AdminUngeocodedVenues /></Protected>} />
      <Route path="/admin/venues/:venueId/edit" element={<Protected adminOnly><AdminVenueEdit /></Protected>} />
      <Route path="/admin/venues/:venueId/team" element={<Protected adminOnly><AdminVenueTeam /></Protected>} />
      <Route path="/admin/audit-log" element={<Protected adminOnly><AdminAuditLog /></Protected>} />
      {AMBASSADOR_ENABLED && <Route path="/admin/ambassadors" element={<Protected adminOnly><AdminAmbassadors /></Protected>} />}
      <Route path="/owner/setup" element={<Protected><OwnerSetupChecklist /></Protected>} />
      <Route path="/admin/workspaces/:workspaceId/edit" element={<Protected adminOnly><AdminWorkspaceEdit /></Protected>} />
      {/* Inventory V2 Stage 3A — admin-preview only, gated by FEATURE_INVENTORY_V2. */}
      <Route path="/locations/:locationSlug" element={<Protected adminOnly><LocationDetail /></Protected>} />
      <Route path="/locations/:locationSlug/workspaces/:resourceSlug" element={<Protected adminOnly><WorkspaceResourceDetail /></Protected>} />
      <Route path="/locations/:locationSlug/venues/:resourceSlug" element={<Protected adminOnly><VenueResourceDetail /></Protected>} />
      <Route path="/partner/:slug" element={<PartnerDashboard />} />
      <Route path="/events" element={<Protected><LocalEvents /></Protected>} />
      <Route path="/events/local/:slug" element={<Protected><EventDetail /></Protected>} />
      <Route path="/owner/dashboard" element={<Protected><OwnerDashboard /></Protected>} />
      <Route path="/owner/onboard" element={<Protected><OwnerOnboarding /></Protected>} />
      <Route path="/reviews/:bookingId" element={<Protected><ReviewForm /></Protected>} />
      {/* Session 60.24 · Member-facing booking detail. Backs the booking
          notification deep-links (booking_request / approved / declined /
          confirmed / cancelled / refunded / payment). Presentation layer
          over /api/bookings/v2/{req_id} — never duplicates booking logic. */}
      <Route path="/account/bookings/:reqId" element={<Protected><MemberBookingDetail /></Protected>} />
      <Route path="/spaces" element={<SpacesDirectory />} />
      <Route path="/dmv" element={<Navigate to="/nh" replace />} />
      <Route path="/nh" element={<Region />} />
      <Route path="/ma" element={<Region />} />
      <Route path="/regions/:region" element={<Region />} />
      <Route path="/support" element={<Support />} />
      <Route path="/contact" element={<Support />} />
      <Route path="/hours" element={<Hours />} />
      <Route path="/llm-info" element={<LlmInfo />} />
      <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
      <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
      <Route path="/legal/terms" element={<LegalDocument kind="terms" />} />
      <Route path="/legal/privacy" element={<LegalDocument kind="privacy" />} />
      <Route path="/list-your-space" element={<ListYourSpace />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/nominate" element={<NominateSpace />} />
      <Route path="/events/submit" element={<Protected><SubmitEvent /></Protected>} />
      {/* Session 42 · /spaces/recmode is a legacy hardcoded route from the
          pre-Inventory-V2 era. The canonical URL is now the venue slug
          (rec-mode-silver-spring). Redirect to prevent the "duplicate Rec-Mode"
          confusion where users landed on a stale hardcoded page and saw a
          separate listing than the one in the venue directory. */}
      <Route path="/spaces/recmode" element={<Navigate to="/spaces/rec-mode-silver-spring" replace />} />
      <Route path="/spaces/recmode/confirmation" element={<RecModeConfirmation />} />
      <Route path="/spaces/recmode/dashboard" element={<Protected adminOnly><RecModeDashboard /></Protected>} />
      <Route path="/bookings/v2/success" element={<BookingV2Success />} />
      <Route path="/bookings/v2/cancel" element={<BookingV2Cancel />} />
      <Route path="/spaces/:slug" element={<SpaceDetail />} />
      <Route path="/spaces/:venueSlug/:workspaceSlug" element={<VenueWorkspaceDetail />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <LegalFooter />
    </BetaGate>
  );
}

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}
